import React from 'react'
import { View, StyleSheet } from 'react-native'
import { useQuery } from 'convex/react'

import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useGameContext } from '../GameContext'
import { useGameState } from '../useGameState'
import { DS } from '../../../design-system'
import { getLeaveGameStatsLines } from '../hooks/useLeaveGameStatsMessage'
import { GameModal } from './GameModalContainer'
import { TournamentWinnerModal } from '@/screens/tournament/TournamentWinnerModal'

interface TournamentGameOverModalProps {
  visible: boolean
  onClose: () => void
  onReturnToLobby: () => void
  title?: string
  message?: string
  buttonLabel?: string
}

export const TournamentGameOverModal = ({
  visible,
  onClose,
  onReturnToLobby,
  title = 'Tournament Finished',
  message = 'The tournament has ended. Review the results before returning to the lobby.',
  buttonLabel = 'View Results'
}: TournamentGameOverModalProps) => {
  const { game } = useGameState()
  const { userId } = useGameContext()
  const tournamentStats = useQuery(
    api.tournaments.queries.getMyTournamentStats,
    game?.tournamentId ? { tournamentId: game.tournamentId as Id<'tournaments'> } : 'skip'
  )
  const tournamentStatus = useQuery(
    api.tournaments.queries.getTournamentStatus,
    game?.tournamentId ? { tournamentId: game.tournamentId as Id<'tournaments'> } : 'skip'
  )

  // Tournament (not just table) finished: celebrate with the winner
  // announcement — crown, confetti, winner name — instead of the plain card.
  // The mid-tournament "Table Finished" variant keeps the old quiet treatment.
  if (tournamentStatus?.state === 'finished' && tournamentStatus.winnerName) {
    return (
      <TournamentWinnerModal
        visible={visible}
        winnerName={tournamentStatus.winnerName}
        isWinner={tournamentStatus.winnerId === userId}
        tournamentName={tournamentStatus.name}
        onViewResults={onReturnToLobby}
        onClose={onClose}
      />
    )
  }

  const statsLines = getLeaveGameStatsLines({
    numberOfHands: tournamentStats?.handsPlayed,
    numberOfHandsWon: tournamentStats?.handsWon,
    rank: tournamentStats?.finalRank ?? null
  })
  const leaveGameStatsMessage = statsLines.join('\n')

  return (
    <GameModal visible={visible} onClose={onClose}>
      <View style={styles.modalContainer}>
        <DS.Frame style={styles.container}>
          <DS.Icon name="Stars" width={48} height={48} color="Brand/Maroon" />
          <DS.Text variant="Heading 2/Medium" textAlign="center">
            {title}
          </DS.Text>
          <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey" textAlign="center">
            {message}
          </DS.Text>
          {leaveGameStatsMessage && (
            <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey" textAlign="center">
              {leaveGameStatsMessage}
            </DS.Text>
          )}
          <DS.Frame style={styles.buttonsContainer}>
            <DS.Button variant="Large/Primary" type="On Light" onPress={onReturnToLobby} style={{ marginTop: 16 }}>
              {buttonLabel}
            </DS.Button>
          </DS.Frame>
        </DS.Frame>
      </View>
    </GameModal>
  )
}

const styles = StyleSheet.create({
  modalContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  container: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    gap: 12,
    width: '100%',
    maxWidth: 340
  },
  buttonsContainer: {
    width: '100%',
    gap: 12
  }
})
